// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useState } from 'react';
import { Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams, GridToolbarContainer } from '@mui/x-data-grid';
import { OperatorConfiguration } from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Configuration/Operator';
import { AsyncButton } from '../../../components/Button/AsyncButton';
import { SyncButton } from '../../../components/Button/SyncButton';
import Loading from '../../../components/Loading';
import PartyDisplay from '../../../components/PartyDisplay';
import { QueryTable } from '../../../components/Table/QueryTable';
import { useParties } from '../../../contexts/PartiesContext';
import useArchiveOperatorConfiguration from '../../../hooks/mutations/registry/configuration/useArchiveOperatorConfiguration';
import useMutate from '../../../hooks/mutations/useMutate';
import useRegistryOperatorConfiguration from '../../../hooks/queries/registry/configuration/useRegistryOperatorConfiguration';
import useRegistryOperatorConfigurations from '../../../hooks/queries/registry/configuration/useRegistryOperatorConfigurations';
import RequiredCredentials from '../../../modules/Credential/RequiredCredentials';
import TemplateContract from '../../../utils/TemplateContract';
import { SUC_ARC_OP_CONF, ERR_ARC_OP_CONF } from '../../../utils/strings';
import { CreateOperatorConfigurationDialog } from './CreateOperatorConfigurationDialog';
import { ModifyOperatorConfigurationDialog } from './ModifyOperatorConfigurationDialog';

type Row = TemplateContract<OperatorConfiguration>;

type Action = {
  name: string;
  action: (r: Row) => Promise<unknown>;
};

const createHeaders: (createActions: (r: Row) => Action[]) => GridColDef<Row>[] = (
  createActions,
) => {
  const renderActionCell = (params: GridRenderCellParams<Row>) => {
    const actions = createActions(params.row);
    return (
      <>
        {actions.map(({ name, action }) => (
          <AsyncButton key={name} sx={{ mr: 1 }} onClick={() => action(params.row)}>
            {name}
          </AsyncButton>
        ))}
      </>
    );
  };

  return [
    {
      field: 'operator',
      headerName: 'Operator',
      renderCell: (params) => <PartyDisplay party={params.row.payload.operator} />,
      flex: 150,
      valueGetter: (params) => params.row.payload.operator,
    },
    {
      field: 'providerRequirements',
      headerName: 'Required Credentials for Providers',
      renderCell: (params) => (
        <RequiredCredentials credentialRequirements={params.row.payload.providerRequirements} />
      ),
      flex: 250,
      filterable: false,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      renderCell: (params) => renderActionCell(params),
      flex: 150,
      filterable: false,
    },
  ];
};

const Toolbar = () => {
  const [createOperatorConfigurationIsOpen, setCreateOperatorConfigurationIsOpen] = useState(false);
  const operatorConfiguration = useRegistryOperatorConfiguration();

  const { isLoading } = operatorConfiguration;

  return (
    <GridToolbarContainer>
      {isLoading ? null : (
        <>
          {createOperatorConfigurationIsOpen && (
            <CreateOperatorConfigurationDialog
              open={createOperatorConfigurationIsOpen}
              onClose={() => setCreateOperatorConfigurationIsOpen(false)}
            />
          )}
          <SyncButton
            onClick={() => setCreateOperatorConfigurationIsOpen(true)}
            disabled={!!operatorConfiguration.data}
          >
            Create operator configuration
          </SyncButton>
        </>
      )}
    </GridToolbarContainer>
  );
};

export const OperatorConfigurations = () => {
  const [modifyOperatorConfiguration, setModifyOperatorConfiguration] = useState<
    TemplateContract<OperatorConfiguration> | undefined
  >();

  const operatorConfigurations = useRegistryOperatorConfigurations();
  const archiveOperatorConfiguration = useArchiveOperatorConfiguration();
  const { operator, party } = useParties();
  const mutate = useMutate();

  const archive = useCallback(
    (row: Row) => {
      const payload = {
        operatorConfigurationCid: row.contractId,
      };
      return mutate(archiveOperatorConfiguration, payload, SUC_ARC_OP_CONF, ERR_ARC_OP_CONF);
    },
    [archiveOperatorConfiguration, mutate],
  );

  const isOperator = operator === party;

  const createActions = (r: Row) => {
    const actions: Action[] = [];
    if (isOperator)
      actions.push(
        { name: 'Modify', action: () => Promise.resolve(setModifyOperatorConfiguration(r)) },
        { name: 'Archive', action: archive },
      );
    return actions;
  };

  const isLoading = operatorConfigurations.isLoading || archiveOperatorConfiguration.isLoading;
  if (isLoading) return <Loading />;

  const hasData = operatorConfigurations.data;
  if (!hasData) return <Typography>No configurations</Typography>;

  return (
    <>
      {!!modifyOperatorConfiguration && (
        <ModifyOperatorConfigurationDialog
          open={!!modifyOperatorConfiguration}
          onClose={() => setModifyOperatorConfiguration(undefined)}
          operatorConfiguration={modifyOperatorConfiguration}
        />
      )}
      <QueryTable
        title="Operator Configurations"
        variant="h3"
        enableTableWrapper
        columns={createHeaders(createActions)}
        rowQuery={operatorConfigurations}
        rowKey={(a) => a.contractId}
        toolbar={Toolbar}
      />
    </>
  );
};
